import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { api } from "../api.ts";
import { type StepUpCalls, StepUpForm, StepUpFrame } from "../security/step-up.tsx";
import { useSession } from "../session.tsx";
import { csrf } from "./queries.ts";

/**
 * Proves the credentials again before a security action (password, with the authenticator
 * code when the account has one, or a passkey). The server then allows security actions on
 * this session for 5 minutes.
 */
export function StepUpDialog(props: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  passkeys: boolean;
  /** A federated account's email: its IdP proves who it is, through a new sign-in. */
  federatedEmail?: string | null;
  /** Where a federated sign-in comes back to (the account page by default). */
  returnTo?: string;
}) {
  const { t } = useTranslation();
  const { federatedEmail } = props;
  return (
    <StepUpFrame
      open={props.open}
      onClose={props.onClose}
      description={federatedEmail ? t("stepUp.federated") : t("stepUp.lead")}
    >
      {federatedEmail ? (
        <DialogFooter>
          <Button
            onClick={() =>
              window.location.assign(
                `/api/auth/login?email=${encodeURIComponent(federatedEmail)}&returnTo=${encodeURIComponent(props.returnTo ?? "/account")}`,
              )
            }
          >
            {t("stepUp.signInAgain")}
          </Button>
        </DialogFooter>
      ) : (
        <AccountStepUpForm {...props} />
      )}
    </StepUpFrame>
  );
}

/** The account's re-authentication routes, under the session's CSRF token. */
function AccountStepUpForm(props: { onClose: () => void; onDone: () => void; passkeys: boolean }) {
  const { csrfToken } = useSession();
  const reauth = api.api.account.reauth;
  const calls: StepUpCalls = {
    password: (json) => reauth.$post({ json }, csrf(csrfToken)),
    // Always: the server may ask for the passkey after the password, whatever the page knows.
    passkey: {
      options: () => reauth.passkey.options.$post(undefined, csrf(csrfToken)),
      verify: (credential) => reauth.passkey.$post({ json: { credential } }, csrf(csrfToken)),
    },
  };
  return (
    <StepUpForm
      calls={calls}
      offerPasskey={props.passkeys}
      onClose={props.onClose}
      onDone={props.onDone}
    />
  );
}
